import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button, Input } from '../components';
import { CheckCircle, Plus, Star, X, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

interface ActionPoint {
  id: string;
  titel_actiepunt: string;
  actie_omschrijving: string;
  start_op: string;
  deadline: string;
  prioriteit: 'laag' | 'normaal' | 'hoog';
  voltooid: boolean;
  employer_id?: string;
  dossier_id?: string;
  employer_first_name?: string;
  employer_last_name?: string;
  employer_company_name?: string;
  dossier_client_name?: string;
}

export const ProfessionalActionItemsPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCompleted, setFilterCompleted] = useState<string>('all');
  const queryClient = useQueryClient();

  // Get active employers
  const { data: employers } = useQuery({
    queryKey: ['active-employers'],
    queryFn: async () => {
      const response = await api.get('/professional/active-employers');
      return response.data.data || [];
    }
  });

  // Get dossiers
  const { data: dossiers } = useQuery({
    queryKey: ['professional-dossiers'],
    queryFn: async () => {
      const response = await api.get('/professional/dossiers');
      return response.data.data || [];
    }
  });

  // Get action points - only show those that have started
  const { data: actionPoints, isLoading } = useQuery({
    queryKey: ['action-points', filterCompleted],
    queryFn: async () => {
      const params: any = {};
      if (filterCompleted === 'completed') params.voltooid = 'true';
      if (filterCompleted === 'pending') params.voltooid = 'false';
      const response = await api.get('/professional/action-points', { params });
      const allPoints = response.data.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return allPoints.filter((ap: ActionPoint) => {
        const startDate = new Date(ap.start_op);
        startDate.setHours(0, 0, 0, 0);
        return startDate <= today;
      });
    }
  });

  const filteredAndSortedActionPoints = useMemo(() => {
    if (!actionPoints) return [];
    let filtered = [...actionPoints];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((ap: ActionPoint) => {
        const employerName = ap.employer_company_name ||
          `${ap.employer_first_name || ''} ${ap.employer_last_name || ''}`.trim() || '';
        const dossierName = ap.dossier_client_name || '';
        const title = ap.titel_actiepunt || '';
        return employerName.toLowerCase().includes(query) ||
               dossierName.toLowerCase().includes(query) ||
               title.toLowerCase().includes(query);
      });
    }
    filtered.sort((a: ActionPoint, b: ActionPoint) => {
      const startA = new Date(a.start_op).getTime();
      const startB = new Date(b.start_op).getTime();
      if (startA !== startB) return startA - startB;
      const deadlineA = new Date(a.deadline).getTime();
      const deadlineB = new Date(b.deadline).getTime();
      return deadlineA - deadlineB;
    });
    return filtered;
  }, [actionPoints, searchQuery]);

  const createActionPoint = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/professional/action-points', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('actionItems.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['action-points'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setShowForm(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('actionItems.createError'));
    }
  });

  const updateActionPoint = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.patch(`/professional/action-points/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('actionItems.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['action-points'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('actionItems.updateError'));
    }
  });

  const toggleComplete = (actionPoint: ActionPoint) => {
    updateActionPoint.mutate({
      id: actionPoint.id,
      data: { voltooid: !actionPoint.voltooid }
    });
  };

  const isOverdue = (deadline: string, voltooid: boolean) => {
    if (voltooid) return false;
    const deadlineDate = new Date(deadline);
    deadlineDate.setHours(23, 59, 59, 999);
    return deadlineDate < new Date();
  };

  const getStatusColor = (deadline: string, voltooid: boolean) => {
    if (voltooid) return 'bg-gray-300';
    return isOverdue(deadline, voltooid) ? 'bg-red-500' : 'bg-green-500';
  };

  const formatEmployerName = (ap: ActionPoint) => {
    if (ap.employer_company_name) return ap.employer_company_name;
    if (ap.employer_first_name || ap.employer_last_name) {
      return `${ap.employer_last_name || ''}, ${ap.employer_first_name || ''}`.trim().replace(/^,\s*|,\s*$/g, '');
    }
    return '-';
  };

  const formatDossierName = (ap: ActionPoint) => {
    if (!ap.dossier_id || !ap.dossier_client_name) return '-';
    return `${ap.dossier_id.substring(0, 3)} - ${ap.dossier_client_name}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('actionItems.title')}</h1>
        <button
          onClick={() => navigate('/professional/cockpit')}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="mb-6 flex gap-4 items-center">
        <select
          className="px-3 py-2 border border-gray-300 rounded-lg"
          value={filterCompleted}
          onChange={(e) => setFilterCompleted(e.target.value)}
        >
          <option value="all">{t('actionItems.filterAll')}</option>
          <option value="pending">{t('actionItems.filterPending')}</option>
          <option value="completed">{t('actionItems.filterCompleted')}</option>
        </select>

        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder={t('actionItems.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {showForm && (
        <ActionPointForm
          employers={employers || []}
          dossiers={dossiers || []}
          selectedEmployer=""
          onSubmit={(data) => createActionPoint.mutate(data)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {isLoading ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">{t('actionItems.loading')}</p>
          </div>
        </Card>
      ) : filteredAndSortedActionPoints.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actionItems.colDossier')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actionItems.colTitle')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actionItems.colMainManager')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actionItems.colForEmployee')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actionItems.colStartDate')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actionItems.colEndDate')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actionItems.colDone')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedActionPoints.map((actionPoint: ActionPoint) => {
                  const statusColor = getStatusColor(actionPoint.deadline, actionPoint.voltooid);
                  return (
                    <tr key={actionPoint.id} className={actionPoint.voltooid ? 'opacity-60' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {actionPoint.prioriteit === 'hoog' && (
                          <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className={`w-4 h-4 rounded ${statusColor}`}></div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDossierName(actionPoint)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {actionPoint.titel_actiepunt}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatEmployerName(actionPoint)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {/* Voor medewerker */}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {new Date(actionPoint.start_op).toLocaleDateString('nl-NL')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {new Date(actionPoint.deadline).toLocaleDateString('nl-NL')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={actionPoint.voltooid}
                          onChange={() => toggleComplete(actionPoint)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="text-center py-12">
            <CheckCircle className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('actionItems.noItemsTitle')}
            </h2>
            <p className="text-gray-600 mb-4">
              {searchQuery ? t('actionItems.noSearchResults') : t('actionItems.noItemsDesc')}
            </p>
            {!searchQuery && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('actionItems.firstActionBtn')}
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

const ActionPointForm: React.FC<{
  employers: any[];
  dossiers: any[];
  selectedEmployer: string;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ employers, dossiers, selectedEmployer, onSubmit, onCancel }) => {
  const { t } = useTranslation('pages');
  const [formData, setFormData] = useState({
    employer_id: selectedEmployer || '',
    dossier_id: '',
    titel_actiepunt: '',
    actie_omschrijving: '',
    start_op: new Date().toISOString().split('T')[0],
    deadline: '',
    prioriteit: 'normaal' as 'laag' | 'normaal' | 'hoog'
  });

  const filteredDossiers = dossiers.filter((d: any) => d.employer_id === formData.employer_id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      employer_id: formData.employer_id || undefined,
      dossier_id: formData.dossier_id || undefined
    });
  };

  return (
    <Card className="mb-6">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('actionItems.formTitle')}</h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('actionItems.employerLabel')}
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.employer_id}
            onChange={(e) => setFormData({ ...formData, employer_id: e.target.value, dossier_id: '' })}
          >
            <option value="">{t('actionItems.noEmployer')}</option>
            {employers.map((emp: any) => (
              <option key={emp.employer_user_id || emp.employer_id} value={emp.employer_user_id || emp.employer_id}>
                {emp.employer_company_name || `${emp.employer_first_name} ${emp.employer_last_name}`}
              </option>
            ))}
          </select>
        </div>

        {formData.employer_id && filteredDossiers.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('actionItems.dossierLabel')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.dossier_id}
              onChange={(e) => setFormData({ ...formData, dossier_id: e.target.value })}
            >
              <option value="">{t('actionItems.noDossier')}</option>
              {filteredDossiers.map((d: any) => (
                <option key={d.id} value={d.id}>{d.client_name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('actionItems.titleLabel')}
          </label>
          <input
            type="text"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.titel_actiepunt}
            onChange={(e) => setFormData({ ...formData, titel_actiepunt: e.target.value })}
            placeholder={t('actionItems.titlePlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('actionItems.descLabel')}
          </label>
          <textarea
            required
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.actie_omschrijving}
            onChange={(e) => setFormData({ ...formData, actie_omschrijving: e.target.value })}
            placeholder={t('actionItems.descPlaceholder')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('actionItems.startLabel')}
            </label>
            <input
              type="date"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.start_op}
              onChange={(e) => setFormData({ ...formData, start_op: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('actionItems.deadlineLabel')}
            </label>
            <input
              type="date"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              min={formData.start_op}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('actionItems.priorityLabel')}
            </label>
            <select
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.prioriteit}
              onChange={(e) => setFormData({ ...formData, prioriteit: e.target.value as 'laag' | 'normaal' | 'hoog' })}
            >
              <option value="laag">{t('actionItems.priorityLow')}</option>
              <option value="normaal">{t('actionItems.priorityNormal')}</option>
              <option value="hoog">{t('actionItems.priorityHigh')}</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('actionItems.cancel')}
          </Button>
          <Button type="submit">
            {t('actionItems.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
};
